package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.FarmaFreelanceBlue
import com.heelion.farmafreelance.models.UserRole

/**
 * Iscrizione in 4 passaggi con Farmy: i titoli, cosa dice Farmy all'inizio di ogni passaggio e
 * l'aiuto sui campi meno chiari (mostrato quando il campo viene toccato).
 */
object RegistrazioneFarmy {
    const val PASSI = 4

    /** Un campo dell'iscrizione: [chiave] come in [AIUTO], [obbligatorio] per andare avanti. */
    data class Campo(val chiave: String, val obbligatorio: Boolean)

    /** I campi di ogni passaggio, nell'ordine in cui compaiono (la guida li segue così). */
    fun campi(ruolo: UserRole, passo: Int): List<Campo> = if (ruolo == UserRole.FARMACIA) {
        when (passo) {
            0 -> listOf(Campo("nomeFarmacia", true), Campo("ragioneSociale", false))
            1 -> listOf(Campo("partitaIva", true), Campo("sdi", false), Campo("pec", false), Campo("direttore", false))
            2 -> listOf(Campo("indirizzo", true), Campo("zona", true), Campo("telefono", false))
            else -> listOf(Campo("email", true), Campo("password", true))
        }
    } else {
        when (passo) {
            0 -> listOf(
                Campo("nome", true), Campo("cognome", true), Campo("dataNascita", true),
                Campo("luogoNascita", true), Campo("cittadinanza", true), Campo("codiceFiscale", true)
            )
            1 -> if (ruolo == UserRole.INFERMIERE) {
                listOf(Campo("iscrizioneOrdine", false), Campo("polizzaRc", false), Campo("competenze", false))
            } else {
                listOf(Campo("iscrizioneOrdine", false), Campo("competenze", false))
            }
            2 -> listOf(Campo("zona", true), Campo("telefono", false))
            else -> listOf(Campo("email", true), Campo("password", true))
        }
    }

    /** "il codice fiscale": come Farmy nomina il campo nelle frasi. */
    fun etichetta(chiave: String, ruolo: UserRole): String = when (chiave) {
        "nome" -> "il nome"
        "cognome" -> "il cognome"
        "dataNascita" -> "la data di nascita"
        "luogoNascita" -> "il luogo di nascita"
        "cittadinanza" -> "la cittadinanza"
        "codiceFiscale" -> "il codice fiscale"
        "iscrizioneOrdine" -> if (ruolo == UserRole.INFERMIERE) "il numero di iscrizione all'OPI" else "il numero di iscrizione all'Ordine"
        "polizzaRc" -> "la polizza RC professionale"
        "competenze" -> "le competenze"
        "zona" -> if (ruolo == UserRole.FARMACIA) "regione, provincia, comune e CAP" else "la zona di lavoro"
        "telefono" -> "il telefono"
        "nomeFarmacia" -> "il nome della farmacia"
        "ragioneSociale" -> "la ragione sociale"
        "partitaIva" -> "la partita IVA"
        "sdi" -> "il codice SDI"
        "pec" -> "la PEC"
        "direttore" -> "il numero di iscrizione del direttore"
        "indirizzo" -> "l'indirizzo"
        "email" -> "l'email"
        "password" -> "la password"
        else -> chiave
    }

    /** Cosa dice Farmy con la guida attiva sul campo [chiave]. */
    fun testoGuida(chiave: String, ruolo: UserRole, obbligatorio: Boolean): String {
        val nome = etichetta(chiave, ruolo).replaceFirstChar { it.uppercase() }
        return "Ora $nome. " + (aiuto(chiave, ruolo)?.let { "$it " } ?: "") +
            if (obbligatorio) "" else "Questo puoi anche saltarlo."
    }

    fun titolo(ruolo: UserRole, passo: Int): String = if (ruolo == UserRole.FARMACIA) {
        listOf("Dati della farmacia", "Dati fiscali e fatturazione", "Indirizzo e contatti", "Credenziali di accesso")[passo]
    } else {
        listOf("Dati anagrafici", "Dati professionali", "Zona di lavoro", "Credenziali di accesso")[passo]
    }

    fun introduzione(ruolo: UserRole, passo: Int): String = if (ruolo == UserRole.FARMACIA) {
        when (passo) {
            0 -> "Benvenuto! Sono Farmy e ti accompagno nell'iscrizione della tua farmacia: sono 4 passaggi brevi. Per cominciare mi servono il nome della farmacia e la ragione sociale."
            1 -> "Ora i dati fiscali: servono per le fatture dei turni. La partita IVA è obbligatoria; codice SDI o PEC servono per la fatturazione elettronica."
            2 -> "Dove si trova la farmacia? Con l'indirizzo ti mostro i farmacisti della tua zona."
            else -> "Ultimo passaggio: email e password con cui accederai all'app."
        }
    } else {
        when (passo) {
            0 -> "Benvenuto! Sono Farmy e ti accompagno nell'iscrizione: sono 4 passaggi brevi. Per cominciare mi servono i tuoi dati anagrafici, come sul codice fiscale."
            1 -> if (ruolo == UserRole.INFERMIERE) {
                "Ora i dati professionali: l'iscrizione all'OPI e la polizza RC servono per verificare che puoi lavorare come infermiere. Dati fiscali e competenze puoi completarli anche dopo, dal profilo."
            } else {
                "Ora i dati professionali: l'iscrizione all'Ordine serve per verificare che sei farmacista. Dati fiscali e competenze puoi completarli anche dopo, dal profilo."
            }
            2 -> "In che zona vuoi lavorare? Ti mostrerò i turni e le farmacie di quella zona."
            else -> "Ultimo passaggio: email e password con cui accederai all'app."
        }
    }

    /** Aiuto sul campo [chiave]: per l'infermiere alcuni campi hanno un testo tutto suo. */
    fun aiuto(chiave: String, ruolo: UserRole): String? =
        (if (ruolo == UserRole.INFERMIERE) AIUTO_INFERMIERE[chiave] else null) ?: AIUTO[chiave]

    private val AIUTO_INFERMIERE = mapOf(
        "iscrizioneOrdine" to "È il numero di iscrizione all'OPI, l'Ordine delle Professioni Infermieristiche della tua provincia: lo trovi sul tesserino. Serve per verificare la tua iscrizione.",
        "polizzaRc" to "Numero e scadenza della tua polizza di responsabilità civile professionale, obbligatoria per lavorare da libero professionista.",
        "enpaf" to "Il contributo integrativo ENPAPI che aggiungi in fattura, di solito il 4%. Serve per calcolare avvisi di pagamento e guadagni.",
        "competenze" to "Scegli cosa sai fare, per esempio medicazioni, iniezioni o ECG: le farmacie cercano gli infermieri anche per competenza."
    )

    /** Aiuto sui campi meno chiari, per nome del campo. */
    val AIUTO = mapOf(
        "dataNascita" to "Tocca l'icona del calendario per sceglierla.",
        "luogoNascita" to "Scrivi le prime lettere del comune e sceglilo dall'elenco.",
        "codiceFiscale" to "16 caratteri: lo trovi sulla tessera sanitaria. Deve corrispondere a nome, cognome, data e luogo di nascita.",
        "iscrizioneOrdine" to "È il numero di iscrizione all'Ordine dei Farmacisti della tua provincia, lo trovi sul tesserino. Serve per verificare la tua iscrizione.",
        "regimeFiscale" to "Forfettario se hai la partita IVA con il regime agevolato, altrimenti ordinario. Nel dubbio chiedi al commercialista: puoi cambiarlo dopo dal profilo.",
        "enpaf" to "La percentuale ENPAF che applichi nelle fatture, di solito il 2%. Serve per calcolare fatture e guadagni.",
        "competenze" to "Scegli cosa sai fare, per esempio galenica, vaccini o tamponi: le farmacie cercano i farmacisti anche per competenza.",
        "zona" to "Scegli regione, provincia e comune: ti mostrerò i turni di quella zona.",
        "telefono" to "Serve per essere contattato sui turni, per esempio quelli urgenti.",
        "ragioneSociale" to "Il nome legale della società, come nella visura camerale: va sulle fatture.",
        "partitaIva" to "La partita IVA della farmacia: 11 cifre.",
        "sdi" to "Il codice destinatario di 7 caratteri per ricevere le fatture elettroniche. Se non lo hai, indica la PEC.",
        "pec" to "La posta elettronica certificata della farmacia, per le fatture elettroniche.",
        "direttore" to "Il numero di iscrizione all'Ordine del direttore della farmacia: serve per verificare la farmacia.",
        "indirizzo" to "Via e numero civico: servono per la mappa e per mostrarti i farmacisti vicini.",
        "email" to "La userai per accedere all'app e per ricevere le comunicazioni.",
        "password" to "Almeno 6 caratteri. Scegline una che non usi altrove."
    )

    const val FINE_TITOLO = "Iscrizione inviata!"

    fun fine(ruolo: UserRole): String =
        "Grazie! Prima di poter entrare, la tua iscrizione deve essere verificata e approvata" +
            when (ruolo) {
                UserRole.FARMACIA -> " (controlliamo i dati della farmacia)."
                UserRole.INFERMIERE -> " (controlliamo l'iscrizione all'OPI e la polizza RC)."
                else -> " (controlliamo l'iscrizione all'Ordine)."
            } +
            " Finché non è approvata, l'accesso ti dirà che l'account è in attesa. " +
            "Quando potrai entrare, la prima volta ti mostrerò io come funziona l'app."
}

/** Il riquadro di Farmy in cima all'iscrizione: passaggio, titolo e cosa dice Farmy (o l'aiuto sul campo toccato). */
@Composable
fun RiquadroFarmyIscrizione(
    passo: Int,
    titolo: String,
    testo: String,
    avviso: String?,
    /** Farmy parla da solo; null = voce non disponibile (niente pulsante). */
    voceAttiva: Boolean?,
    onVoce: (Boolean) -> Unit,
    /** Guida campo per campo: attiva, e i comandi per accenderla e andare al campo dopo. */
    guida: Boolean = false,
    onGuida: (Boolean) -> Unit = {},
    campoGuidatoFacoltativo: Boolean = false,
    onCampoSuccessivo: (() -> Unit)? = null
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F4FD))
    ) {
        Column(Modifier.padding(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                AvatarAssistente(Modifier.size(40.dp))
                Spacer(Modifier.width(10.dp))
                Column(Modifier.weight(1f)) {
                    Text("Passaggio ${passo + 1} di ${RegistrazioneFarmy.PASSI}", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Text(titolo, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = FarmaFreelanceBlue)
                }
                if (voceAttiva != null) {
                    // Interruttore della voce: Farmy parla da solo, qui lo si zittisce o si riattiva.
                    IconButton(onClick = { onVoce(!voceAttiva) }) {
                        Icon(
                            AppIcons.VolumeUp,
                            contentDescription = if (voceAttiva) "Zittisci Farmy" else "Riattiva la voce di Farmy",
                            tint = if (voceAttiva) FarmaFreelanceBlue else Color.Gray.copy(alpha = 0.4f)
                        )
                    }
                }
            }
            LinearProgressIndicator(
                progress = { (passo + 1f) / RegistrazioneFarmy.PASSI },
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
            )
            Text(testo, style = MaterialTheme.typography.bodyMedium)
            avviso?.let {
                Spacer(Modifier.height(6.dp))
                Text(it, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold)
            }
            Spacer(Modifier.height(6.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (guida) {
                    TextButton(onClick = { onGuida(false) }) { Text("Faccio da solo") }
                    Spacer(Modifier.weight(1f))
                    if (onCampoSuccessivo != null) {
                        OutlinedButton(onClick = onCampoSuccessivo) { Text(if (campoGuidatoFacoltativo) "Salta" else "Campo dopo") }
                    }
                } else {
                    OutlinedButton(onClick = { onGuida(true) }) { Text("Guidami tu, campo per campo") }
                }
            }
        }
    }
}
